import React, { ReactNode, useState, useEffect, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import '../styles/Layout.css';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { consultationWhatsAppUrl } from '../data/whatsapp';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const whatsappUrl = consultationWhatsAppUrl();

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const touchStartScroll = useRef(0);
  const isPulling = useRef(false);

  useEffect(() => {
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    if (isCoarsePointer) {
      return undefined;
    }

    const handleTouchStart = (e: TouchEvent) => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Only enable pull-to-refresh at the top of the page
      if (scrollTop === 0) {
        touchStartY.current = e.touches[0].clientY;
        touchStartScroll.current = scrollTop;
        isPulling.current = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === 0) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Only allow pull-to-refresh at the top
      if (scrollTop === 0) {
        const touchY = e.touches[0].clientY;
        const deltaY = touchY - touchStartY.current;

        if (deltaY > 0) {
          isPulling.current = true;
          const distance = Math.min(deltaY * 0.5, 100); // Limit to 100px
          setPullDistance(distance);
          
          // Prevent default scrolling when pulling
          if (distance > 10) {
            e.preventDefault();
          }
        }
      }
    };

    const handleTouchEnd = () => {
      if (isPulling.current && pullDistance > 50) {
        // Trigger refresh
        setIsRefreshing(true);
        setPullDistance(0);
        
        // Simulate refresh (you can replace this with actual refresh logic)
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        // Reset if not enough pull
        setPullDistance(0);
      }
      
      touchStartY.current = 0;
      isPulling.current = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <div className="layout">
      {/* Pull to Refresh Indicator */}
      {pullDistance > 0 && (
        <div 
          className="pull-to-refresh-indicator"
          style={{ 
            transform: `translateY(${pullDistance}px)`,
            opacity: Math.min(pullDistance / 50, 1)
          }}
        >
          <div className="refresh-spinner">
            {pullDistance > 50 ? '🔄' : '⬇️'}
          </div>
          <div className="refresh-text">
            {pullDistance > 50 ? 'حرر للتحديث' : 'اسحب للتحديث'}
          </div>
        </div>
      )}
      
      {isRefreshing && (
        <div className="refreshing-overlay">
          <div className="refresh-spinner-large">🔄</div>
          <div className="refresh-text">جاري التحديث...</div>
        </div>
      )}

      <Header />
      <main className="main-content">
        {children}
      </main>
      <Footer />
      
      {/* WhatsApp Floating Button */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label={t.footer.whatsapp}
        title={t.footer.whatsapp}
      >
        <span className="whatsapp-icon" role="img" aria-label="WhatsApp">💬</span>
        <span className="whatsapp-text">{t.footer.whatsapp}</span>
      </a>
    </div>
  );
};

export default Layout;

